import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type {
	RoadmapFilterNegations,
	RoadmapSortOrder,
	RoadmapSource,
} from "@productlane/zero/queries"
import type { CustomView } from "@productlane/zero/schema"
import { getPortalUrl } from "@productlane/lib/src/domain"
import { mutators } from "@productlane/zero/mutators"
import {
	matchesRoadmapLabels,
	matchesRoadmapSource,
	queries,
	roadmapSortOrderSchema,
} from "@productlane/zero/queries"

import { ConnectLinear } from "@/components/connect-linear"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { useSetBackNavigation } from "@/lib/back-navigation"
import { generateId } from "@/lib/id"
import { useSortedIssues } from "@/lib/issues"
import { hasLinearConnection } from "@/lib/linear-connection"
import { portalColorSeeds } from "@/lib/portal-instances"
import { useSortedProjects } from "@/lib/projects"
import { useRoadmapState, useSortState } from "@/lib/states/global-state"
import { useSelectedTeamIds } from "@/lib/use-team-filter"
import {
	mutateOk,
	reportServerRejection,
	useZero,
	useZeroWorkspaceQuery,
} from "@/lib/zero"
import { RoadmapSettings } from "@/screens/app/roadmap/roadmap-settings"
import { useActivePortalInstance } from "@/stores/active-portal-instance-store"
import { SelectLinearIssuesDialog } from "../changelog/select-linear-issues-dialog"
import { CreateIssueDialog } from "../issues/create-issue-dialog"
import { PageHeader } from "../page-header"
import { PublishPortalPageButton } from "../portal/publish-portal-page-button"
import { CreateProjectDialog } from "../projects/create-project-dialog"
import { defaultRoadmapTab } from "./default-tab"
import { RoadmapFilter } from "./filter/roadmap-filter"
import { RoadmapFilterControl } from "./filter/roadmap-filter-control"
import { useRoadmapFilterState } from "./filter/roadmap-filter-state"
import {
	expandTeamIds,
	projectMatchesTeams,
} from "./filter/roadmap-team-groups"
import {
	fromRoadmapViewFilters,
	useActiveRoadmapView,
	useRoadmapViewUrlSync,
} from "./filter/roadmap-views"
import { useRoadmapFilterUrlSync } from "./filter/use-filter-url-sync"
import { useRoadmapTeams } from "./filter/use-roadmap-teams"
import { itemInPortal } from "./portal-scope"
import { RoadmapNav } from "./roadmap-nav"
import { TabContent } from "./tab-content"

export const OPEN_ISSUE_STATUS = [
	"started",
	"unstarted",
	"backlog",
	"paused",
	"planned",
]

interface RoadmapQueryFilters {
	statuses?: string[]
	teamIds?: string[]
	projectIds?: Array<string | null>
	priorities?: string[]
	companyIds?: Array<string | null>
	sources?: RoadmapSource[]
	labelIds?: string[]
	negations?: RoadmapFilterNegations
}

type RoadmapTab = "active" | "hidden" | "all" | "pending"

const ROADMAP_PAGE_SIZE = 200
const PENDING_COUNT_CAP = 500

const roadmapQueries = (
	tab: "active" | "hidden" | "pending" | "all",
	filters?: RoadmapQueryFilters,
	limit?: number,
	sortOrder?: RoadmapSortOrder,
	scoreDirection?: "asc" | "desc",
) => ({
	issues: queries.roadmapIssues({
		tab,
		filters,
		limit,
		sortOrder,
		scoreDirection,
	}),
	projects: queries.roadmapProjects({ tab, filters, limit, sortOrder }),
})

export const roadmapPagePreloadQueries = () => {
	const q = roadmapQueries("active", undefined, ROADMAP_PAGE_SIZE)
	return [q.issues, q.projects]
}

const ISSUE_PENDING_EXCLUDED_STATUSES = new Set([
	"triage",
	"duplicate",
	"canceled",
	"completed",
])
const PROJECT_PENDING_EXCLUDED_STATES = new Set([
	"canceled",
	"completed",
	"closed",
])

function issueMatchesTab(
	issue: { isVisible?: boolean | null; status?: string | null },
	tab: RoadmapTab,
): boolean {
	switch (tab) {
		case "active":
			return issue.isVisible === true
		case "hidden":
			return issue.isVisible === false
		case "all":
			return issue.isVisible === true || issue.isVisible === false
		case "pending":
			return (
				issue.isVisible == null &&
				!ISSUE_PENDING_EXCLUDED_STATUSES.has(issue.status ?? "")
			)
	}
}

function projectMatchesTab(
	project: { isVisible?: boolean | null; state?: string | null },
	tab: RoadmapTab,
): boolean {
	switch (tab) {
		case "active":
			return project.isVisible === true
		case "hidden":
			return project.isVisible === false
		case "all":
			return project.isVisible === true || project.isVisible === false
		case "pending":
			return (
				project.isVisible == null &&
				!PROJECT_PENDING_EXCLUDED_STATES.has(project.state ?? "")
			)
	}
}

export const RoadmapScreen: FC = () => {
	useSetBackNavigation({ type: "edit-requests", url: "/edit-requests" })
	const { lastPage, setInboxState } = useRoadmapState()
	const [tabValue, setTabValue] = useState<RoadmapTab>(
		lastPage === "hidden" || lastPage === "pending" || lastPage === "all"
			? lastPage
			: "active",
	)
	const [isOpen, setIsOpen] = useState(false)
	const zero = useZero()
	const { workspace, isLoaded: isWorkspaceLoaded } = useZeroWorkspaceQuery()

	useRoadmapFilterUrlSync()
	const {
		statuses,
		teamIds,
		projectIds,
		priorities,
		companyIds,
		sources,
		labelIds,
		negations,
		reset: clearFilters,
	} = useRoadmapFilterState()
	const teams = useRoadmapTeams()
	const expandedTeamIds = useMemo(
		() => expandTeamIds(teams, teamIds),
		[teams, teamIds],
	)
	const filters = useMemo<RoadmapQueryFilters | undefined>(() => {
		if (
			statuses.length === 0 &&
			teamIds.length === 0 &&
			projectIds.length === 0 &&
			priorities.length === 0 &&
			companyIds.length === 0 &&
			sources.length === 0 &&
			labelIds.length === 0
		)
			return undefined
		return {
			negations,
			statuses: statuses.length > 0 ? statuses : undefined,
			teamIds: expandedTeamIds.length > 0 ? expandedTeamIds : undefined,
			projectIds: projectIds.length > 0 ? projectIds : undefined,
			priorities: priorities.length > 0 ? priorities : undefined,
			companyIds: companyIds.length > 0 ? companyIds : undefined,
			sources: sources.length > 0 ? sources : undefined,
			labelIds: labelIds.length > 0 ? labelIds : undefined,
		}
	}, [
		sources,
		labelIds,
		statuses,
		teamIds,
		projectIds,
		priorities,
		companyIds,
		expandedTeamIds,
		negations,
	])

	const [pageSize, setPageSize] = useState(ROADMAP_PAGE_SIZE)
	useEffect(() => {
		setPageSize(ROADMAP_PAGE_SIZE)
	}, [tabValue, filters])

	const roadmapSortOrder: RoadmapSortOrder = roadmapSortOrderSchema
		.catch("SCORE_DESC")
		.parse(workspace?.roadmapSettings?.sortOrder)

	const groupByImportance = useSortState((state) => state.groupByImportance)
	const scoreDirection = groupByImportance ? "desc" : "asc"

	const tabQ = roadmapQueries(
		tabValue,
		filters,
		pageSize,
		roadmapSortOrder,
		scoreDirection,
	)
	const [tabIssues, tabIssuesResult] = useQuery(tabQ.issues)
	const [tabProjects, tabProjectsResult] = useQuery(tabQ.projects)

	// Unfiltered on purpose: the pending count says how much waits for review on
	// this portal, so a status or team filter must never make the tab disappear.
	const pendingQ = roadmapQueries(
		"pending",
		undefined,
		PENDING_COUNT_CAP,
		roadmapSortOrder,
		scoreDirection,
	)
	const [pendingIssues, pendingIssuesResult] = useQuery(pendingQ.issues)
	const [pendingProjects, pendingProjectsResult] = useQuery(pendingQ.projects)

	const issues = useMemo(
		() =>
			tabIssues.filter(
				(i) =>
					issueMatchesTab(i, tabValue) &&
					matchesRoadmapSource(i, sources) &&
					matchesRoadmapLabels(i, filters),
			),
		[tabIssues, tabValue, sources, filters],
	)
	const projects = useMemo(
		() =>
			tabProjects.filter(
				(p) =>
					projectMatchesTab(p, tabValue) &&
					projectMatchesTeams(p, expandedTeamIds) &&
					matchesRoadmapSource(p, sources) &&
					matchesRoadmapLabels(p, filters),
			),
		[tabProjects, tabValue, expandedTeamIds, sources, filters],
	)
	const portalInstances = useMemo(
		() => workspace?.portalInstances ?? [],
		[workspace?.portalInstances],
	)
	// Multi-portal active as soon as 1+ portal beyond the implicit root exists.
	const multiPortalActive = portalInstances.length >= 1
	const [activePortalInstanceId, setActivePortalInstanceId] =
		useActivePortalInstance(portalInstances)
	const [portalScoped, setPortalScoped] = useState(false)
	// The publish button links to the open portal page, or Main from a view.
	const activePortalSlug = portalScoped
		? (portalInstances.find(
				(instance) => instance.id === activePortalInstanceId,
			)?.slug ?? null)
		: null

	const colorSeeds = useMemo(
		() => portalColorSeeds(portalInstances),
		[portalInstances],
	)
	const portalOptions = useMemo(
		() => [
			{
				id: null as string | null,
				name: "Main portal",
				logoUrl: workspace?.logoUrl ?? null,
				colorSeed: colorSeeds.get(null) ?? null,
				url: workspace ? getPortalUrl(workspace, "/request", null) : null,
			},
			...[...portalInstances]
				.sort((a, b) => a.name.localeCompare(b.name))
				.map((p) => ({
					id: p.id,
					name: p.name,
					logoUrl: p.logoUrl ?? null,
					colorSeed: colorSeeds.get(p.id) ?? null,
					url: workspace
						? getPortalUrl(workspace, "/request", {
								slug: p.slug,
								customDomain: p.customDomain,
							})
						: null,
				})),
		],
		[portalInstances, workspace, colorSeeds],
	)

	// Issue/Project portal scoping:
	//   - Main: array is empty (legacy) OR contains the MAIN sentinel.
	//   - Named X: array contains X.
	// Items can now be on Main AND named portals simultaneously by including
	// MAIN_PORTAL_INSTANCE_ID alongside other ids.
	const matchesPortal = useCallback(
		(item: { portalInstanceIds?: readonly string[] | string[] | null }) => {
			if (!multiPortalActive) return true
			return itemInPortal(item.portalInstanceIds, activePortalInstanceId)
		},
		[multiPortalActive, activePortalInstanceId],
	)

	const selectedTeamIds = useSelectedTeamIds()

	// One queue for every portal: a pending item isn't on any portal yet.
	const pendingCount = useMemo(() => {
		const inSelectedTeams = (item: {
			team?: string | null
			linearTeamIds?: readonly string[] | null
		}) => {
			if (selectedTeamIds.length === 0) return true
			if (item.linearTeamIds)
				return item.linearTeamIds.some((id) => selectedTeamIds.includes(id))
			return !!item.team && selectedTeamIds.includes(item.team)
		}
		return (
			pendingIssues.filter(
				(i) => inSelectedTeams(i) && issueMatchesTab(i, "pending"),
			).length +
			pendingProjects.filter(
				(p) => inSelectedTeams(p) && projectMatchesTab(p, "pending"),
			).length
		)
	}, [pendingIssues, pendingProjects, selectedTeamIds])

	// Once per visit: the tab follows the pending count until the user picks one.
	const tabChosen = useRef(false)
	const isPendingLoaded =
		pendingIssuesResult.type === "complete" &&
		pendingProjectsResult.type === "complete"
	useEffect(() => {
		if (tabChosen.current || !isPendingLoaded) return
		tabChosen.current = true
		const tab = defaultRoadmapTab({ lastPage, pendingCount })
		setTabValue(tab)
		setInboxState(tab)
	}, [isPendingLoaded, lastPage, pendingCount, setInboxState])

	// The views (pending, visible, hidden, all) span every portal. A portal page
	// is the visible view narrowed to what that one portal shows.
	const portalScopedIssues = useMemo(
		() => (portalScoped ? issues.filter(matchesPortal) : issues),
		[portalScoped, issues, matchesPortal],
	)
	const portalScopedProjects = useMemo(
		() => (portalScoped ? projects.filter(matchesPortal) : projects),
		[portalScoped, projects, matchesPortal],
	)

	const isLoading =
		tabIssuesResult.type !== "complete" || tabProjectsResult.type !== "complete"

	const hasMoreRows =
		tabIssues.length >= pageSize || tabProjects.length >= pageSize
	const loadMoreRows = useCallback(() => {
		setPageSize((size) => size + ROADMAP_PAGE_SIZE)
	}, [])

	// Projects
	const { maxMap: maxMapProjects, sortedProjects: filteredProjects } =
		useSortedProjects({
			projects: portalScopedProjects,
		})

	// Issues
	const { sortedIssues: augmentedIssues } = useSortedIssues({
		issues: portalScopedIssues,
	})

	const [isAddOpen, setIsAddOpen] = useState(false)
	const [isCreateIssueDialogOpen, setIsCreateIssueDialogOpen] = useState(false)

	const createProject = useCallback(() => {
		setIsAddOpen(true)
		analytics.track("open-create-project-dialog")
	}, [])

	const createIssue = useCallback(() => {
		setIsCreateIssueDialogOpen(true)
		analytics.track("open-create-issue-dialog")
	}, [])

	const activeViewId = useActiveRoadmapView((s) => s.activeViewId)
	const setActiveViewId = useActiveRoadmapView((s) => s.setActiveViewId)
	const [roadmapViews] = useQuery(queries.roadmapViews())
	const activeView = roadmapViews.find((view) => view.id === activeViewId)

	// Leaving a view drops its filters, the way Linear swaps a view's filters out.
	const leaveView = useCallback(() => {
		if (!useActiveRoadmapView.getState().activeViewId) return
		setActiveViewId(null)
		clearFilters()
	}, [setActiveViewId, clearFilters])

	const openView = useCallback(
		(view: CustomView) => {
			useRoadmapFilterState.setState(
				fromRoadmapViewFilters(view.filters.roadmap),
			)
			setActiveViewId(view.id)
			tabChosen.current = true
			setTabValue("all")
			setInboxState("all")
			setPortalScoped(false)
		},
		[setActiveViewId, setInboxState],
	)
	useRoadmapViewUrlSync(roadmapViews, activeView, openView)

	const handleTabChange = useCallback(
		(value: string) => {
			const newValue: RoadmapTab =
				value === "hidden" || value === "pending" || value === "all"
					? value
					: "active"
			leaveView()
			tabChosen.current = true
			setTabValue(newValue)
			setInboxState(newValue)
			setPortalScoped(false)
		},
		[setInboxState, leaveView],
	)

	const openPortal = useCallback(
		(portalId: string | null) => {
			leaveView()
			setActivePortalInstanceId(portalId)
			setPortalScoped(true)
			tabChosen.current = true
			setTabValue("active")
		},
		[setActivePortalInstanceId, leaveView],
	)

	const previousCompanyIdsCount = useRef(companyIds.length)
	useEffect(() => {
		if (previousCompanyIdsCount.current === 0 && companyIds.length > 0) {
			tabChosen.current = true
			setTabValue("all")
			setInboxState("all")
		}
		previousCompanyIdsCount.current = companyIds.length
	}, [companyIds.length, setInboxState])

	return (
		<>
			<SelectLinearIssuesDialog
				excludeStatus={["canceled"]}
				isOpen={isOpen}
				onOpenChange={setIsOpen}
				onSave={async (issues) => {
					// Note: This is a server-side signal mutation
					await mutateOk(
						reportServerRejection(
							zero.mutate(
								mutators.issues.upsert({
									linearIssues: issues.map((issue) => ({
										...issue,
										dueDate: issue.dueDate ?? null,
										internalIssueId: generateId(),
										state: {
											type: issue.state.type,
											name: issue.state.name ?? "Unknown",
											color: issue.state.color ?? "#gray",
										},
									})),
									internalUpdate: {
										isVisible: true,
										notUpdateVisibility: false,
									},
								}),
							),
							(message) => {
								toast.error({
									title: "Couldn't import these issues",
									description: message,
								})
							},
						),
					)
				}}
				filterImportedIssues={true}
				title="Import issues"
			/>
			<CreateIssueDialog
				isOpen={isCreateIssueDialogOpen}
				onOpenChange={async (value, created) => {
					setIsCreateIssueDialogOpen(value)
					if (!value) {
						analytics.track("close-create-issue-dialog")
					}
					if (created && created.internalUpdate) {
						// Add the created issue to the roadmap with the correct visibility
						await mutateOk(
							zero.mutate(
								mutators.issues.upsert({
									linearIssues: [
										{
											id: created.id,
											identifier: created.identifier,
											title: created.title,
											url: created.url,
											description: created.description ?? null,
											dueDate:
												created.dueDate && typeof created.dueDate === "string"
													? created.dueDate
													: null,
											internalIssueId: generateId(),
											state: {
												type: created.state?.type ?? "backlog",
												name: created.state?.name ?? "Unknown",
												color: created.state?.color ?? "#gray",
											},
											priorityLabel: created.priorityLabel ?? "No priority",
											team: {
												id: created.team?.id || "",
											},
											projectId: created.projectId || undefined,
										},
									],
									internalUpdate: {
										isVisible: created.internalUpdate.isVisible ?? false,
										notUpdateVisibility: false,
									},
								}),
							),
						)
					}
				}}
			/>
			<MetaInfo title="Feature requests" />
			<CreateProjectDialog
				isOpen={isAddOpen}
				onOpenChange={(value) => {
					setIsAddOpen(value)
					if (!value) {
						analytics.track("close-create-project-dialog")
					}
				}}
			/>
			<div className="flex min-h-0 w-full flex-1 overflow-hidden">
				<RoadmapNav
					tabValue={tabValue}
					onTabChange={handleTabChange}
					portals={portalOptions}
					activePortalId={portalScoped ? activePortalInstanceId : undefined}
					onPortalChange={openPortal}
					pendingCount={pendingCount}
					isPendingCountCapped={
						pendingIssues.length >= PENDING_COUNT_CAP ||
						pendingProjects.length >= PENDING_COUNT_CAP
					}
					onCreateProject={createProject}
					onCreateIssue={createIssue}
					onImportIssues={() => setIsOpen(true)}
					isLinearConnected={hasLinearConnection(workspace)}
					onOpenView={openView}
				/>
				<div className="flex flex-shrink flex-grow flex-col overflow-hidden">
					{!isWorkspaceLoaded ? (
						<div className="flex-1" />
					) : hasLinearConnection(workspace) ? (
						<>
							<PageHeader
								title={
									<RequestsHeaderLabel
										tabValue={tabValue}
										viewName={activeView?.name}
										portalName={
											portalScoped
												? portalOptions.find(
														(p) => p.id === activePortalInstanceId,
													)?.name
												: undefined
										}
									/>
								}
								titleClassName="text-sm font-medium"
								className="pl-[4.75rem] pr-3 sm:pl-2 lg:pl-[17.5px]"
								actions={
									<div className="flex items-center gap-2">
										<RoadmapFilter />
										<PublishPortalPageButton
											content={<RoadmapSettings />}
											isPublished={workspace.domainPublic}
											url={getPortalUrl(
												workspace,
												"/request",
												activePortalSlug,
											)}
											name="roadmap"
										/>
									</div>
								}
							/>
							<RoadmapFilterControl />
							<TabContent
								filteredProjects={filteredProjects}
								issues={augmentedIssues}
								maxMapProjects={maxMapProjects}
								tabValue={tabValue}
								onTabChange={handleTabChange}
								isLoading={isLoading}
								workspaceId={workspace?.id}
								hasMoreRows={hasMoreRows}
								onLoadMoreRows={loadMoreRows}
								pendingCount={pendingCount}
								hasActiveFilters={filters !== undefined}
								onClearFilters={clearFilters}
							/>
						</>
					) : (
						<>
							<PageHeader
								titleClassName="text-sm font-medium"
								className="h-12 max-h-12 min-h-12 border-b py-0 pl-[4.75rem] pr-3 sm:pl-[17.5px]"
								actions={
									<div className="ml-auto flex flex-wrap items-center justify-center gap-2">
										<RoleCheck not={["VIEWER"]}>
											{workspace && (
												<PublishPortalPageButton
													name="roadmap"
													isPublished={workspace.domainPublic || false}
													content={<RoadmapSettings />}
													url={getPortalUrl(
														workspace,
														"/request",
														activePortalSlug,
													)}
												/>
											)}
										</RoleCheck>
									</div>
								}
							/>
							<ConnectLinear
								title="Feature requests"
								description="Share Linear requests with customers on a public or private portal. If you activate the support portal, customers will see all Linear projects and issues that are assigned to them."
								variant="portal"
							/>
						</>
					)}
				</div>
			</div>
		</>
	)
}

const TAB_LABELS: Record<RoadmapTab, string> = {
	active: "Visible",
	hidden: "Hidden",
	all: "All requests",
	pending: "Pending",
}

function RequestsHeaderLabel({
	tabValue,
	portalName,
	viewName,
}: {
	tabValue: RoadmapTab
	portalName?: string
	viewName?: string
}) {
	return (
		<span className="text-sm font-medium">
			{viewName ?? portalName ?? TAB_LABELS[tabValue]}
		</span>
	)
}
